import { Button, cn, toast } from '@startupquickstart/frontend';
import { Loader2, Paperclip, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useUploadAttachment, type TicketAttachment } from '@/api/support';

interface LinearAttachmentInputProps {
  /** Current attachments. Source-of-truth lives on the parent form. */
  value: TicketAttachment[];
  /** Replace the attachment list. */
  onChange: (next: TicketAttachment[]) => void;
  /** Cap on the number of attachments — defaults to the API's limit of 10. */
  max?: number;
  /** Cap on individual file size — defaults to the API's 50MB. */
  maxBytes?: number;
}

const DEFAULT_MAX = 10;
const DEFAULT_MAX_BYTES = 50 * 1024 * 1024;

/**
 * Drag-drop / click / clipboard-paste file uploader that pushes bytes
 * directly to Linear via the signed-URL flow. The form never sees the
 * file bytes — only the Linear `assetUrl`.
 *
 * Why a custom component instead of `ImageUploadDropzone` from the
 * shared frontend? That component targets our own S3 endpoints — Linear's
 * upload-then-reference flow is a different shape (sign → PUT → record).
 */
export function LinearAttachmentInput({
  value,
  onChange,
  max = DEFAULT_MAX,
  maxBytes = DEFAULT_MAX_BYTES,
}: LinearAttachmentInputProps) {
  const upload = useUploadAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeUploads, setActiveUploads] = useState(0);

  /** Listen for clipboard paste anywhere on the page while this input is mounted. */
  useEffect(() => {
    const handler = (event: ClipboardEvent) => {
      const items = event.clipboardData?.files;
      if (!items || items.length === 0) return;
      void uploadFiles(Array.from(items));
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, max, maxBytes]);

  async function uploadFiles(files: File[]) {
    const remaining = Math.max(0, max - value.length);
    if (remaining === 0) {
      toast.error(`Maximum of ${max} attachments reached`);
      return;
    }
    const accepted = files.slice(0, remaining);

    const tooLarge = accepted.find(f => f.size > maxBytes);
    if (tooLarge) {
      toast.error(
        `${tooLarge.name} is too large (${Math.round(tooLarge.size / 1024 / 1024)}MB; max ${Math.round(maxBytes / 1024 / 1024)}MB)`,
      );
      return;
    }

    setActiveUploads(n => n + accepted.length);
    try {
      const uploaded: TicketAttachment[] = [];
      for (const file of accepted) {
        try {
          const attachment = await upload.mutateAsync(file);
          uploaded.push(attachment);
        } catch (err) {
          toast.error(`${file.name}: ${err instanceof Error ? err.message : 'upload failed'}`);
        }
      }
      if (uploaded.length > 0) {
        onChange([...value, ...uploaded]);
      }
    } finally {
      setActiveUploads(n => Math.max(0, n - accepted.length));
    }
  }

  function removeAt(index: number) {
    const next = [...value];
    next.splice(index, 1);
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <div
        className={cn(
          'rounded-lg border border-dashed border-input p-6 text-center text-sm transition-colors',
          isDragging && 'border-primary bg-primary/5',
        )}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setIsDragging(false);
          void uploadFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Paperclip className="h-5 w-5" />
          <p>
            Drag files, paste a screenshot, or{' '}
            <button
              type="button"
              className="font-medium text-primary underline-offset-2 hover:underline"
              onClick={() => fileInputRef.current?.click()}
            >
              browse
            </button>
            .
          </p>
          <p className="text-xs">
            Up to {max} files · max {Math.round(maxBytes / 1024 / 1024)}MB each
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => {
            void uploadFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </div>

      {(value.length > 0 || activeUploads > 0) && (
        <ul className="space-y-2">
          {value.map((att, i) => (
            <li
              key={`${att.assetUrl}-${i}`}
              className="flex items-start justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm"
            >
              <div className="flex min-w-0 items-start gap-2">
                <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                {/* break-all so a long, space-less filename (e.g. a UUID path)
                    wraps within the row instead of forcing the modal to scroll
                    horizontally. */}
                <a
                  href={att.assetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 break-all hover:underline"
                >
                  {att.filename}
                </a>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => removeAt(i)}
                aria-label={`Remove ${att.filename}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
          {activeUploads > 0 && (
            <li className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading {activeUploads} file{activeUploads === 1 ? '' : 's'}…
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
